import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '../lib/logger';

export type NotificationType = 'new_booking' | 'confirmed' | 'cancelled' | 'birthday' | 'new_waitlist';

export interface AppNotification {
  id: string;
  organization_id?: string | null;
  salon_id: string;
  type: NotificationType;
  title: string;
  body: string;
  appointment_id?: string | null;
  client_id?: string | null;
  notification_date?: string;
  read: boolean;
  created_at: string;
}

type ToneStep = {
  freq: number;
  at: number;
  duration: number;
  gain?: number;
  wave?: OscillatorType;
  decay?: 'linear' | 'exponential';
};

const NOTIFICATION_FETCH_LIMIT = 100;
const STALE_NOTIFICATION_THRESHOLD_MS = 30_000;
const NOTIFICATIONS_REFRESH_INTERVAL_MS = 8_000;
const BIRTHDAY_SYNC_INTERVAL_MS = 5 * 60_000;
const INSTANT_BOOKING_PREFIX = 'instant-booking-';
const MOJIBAKE_PATTERN = /[ÃÂ]/;

const TONE_STEPS: Record<NotificationType, ToneStep[]> = {
  new_booking: [
    { freq: 1046.5, at: 0, duration: 0.7, gain: 0.18, wave: 'sine' },
    { freq: 1318.51, at: 0.015, duration: 0.58, gain: 0.14, wave: 'triangle' },
    { freq: 2093, at: 0.025, duration: 0.5, gain: 0.12, wave: 'sine' },
    { freq: 1318.51, at: 0.2, duration: 0.52, gain: 0.16, wave: 'sine' },
    { freq: 2637.02, at: 0.22, duration: 0.42, gain: 0.1, wave: 'triangle' },
  ],
  confirmed: [
    { freq: 987.77, at: 0, duration: 0.42, gain: 0.15, wave: 'sine' },
    { freq: 1318.51, at: 0.11, duration: 0.44, gain: 0.15, wave: 'triangle' },
    { freq: 1760, at: 0.22, duration: 0.52, gain: 0.13, wave: 'sine' },
  ],
  cancelled: [
    { freq: 392, at: 0, duration: 0.28, gain: 0.16, wave: 'triangle' },
    { freq: 293.66, at: 0.2, duration: 0.42, gain: 0.16, wave: 'sine' },
    { freq: 220, at: 0.36, duration: 0.5, gain: 0.12, wave: 'triangle' },
  ],
  birthday: [
    { freq: 783.99, at: 0, duration: 0.28, gain: 0.13, wave: 'sine' },
    { freq: 987.77, at: 0.14, duration: 0.28, gain: 0.14, wave: 'triangle' },
    { freq: 1174.66, at: 0.28, duration: 0.3, gain: 0.14, wave: 'sine' },
    { freq: 1568, at: 0.48, duration: 0.55, gain: 0.13, wave: 'triangle' },
  ],
  new_waitlist: [
    { freq: 880, at: 0, duration: 0.32, gain: 0.15, wave: 'sine' },
    { freq: 1108.73, at: 0.13, duration: 0.34, gain: 0.13, wave: 'triangle' },
    { freq: 1396.91, at: 0.26, duration: 0.4, gain: 0.12, wave: 'sine' },
  ],
};

let sharedAudioContext: AudioContext | null = null;
let audioUnlocked = false;
const utf8Decoder = typeof TextDecoder !== 'undefined'
  ? new TextDecoder('utf-8', { fatal: false })
  : null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  try {
    type AnyWindow = Window & { webkitAudioContext?: typeof AudioContext };
    const AudioContextCtor = window.AudioContext ?? (window as AnyWindow).webkitAudioContext;
    if (!AudioContextCtor) return null;

    if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
      sharedAudioContext = new AudioContextCtor();
    }

    return sharedAudioContext;
  } catch {
    return null;
  }
}

function unlockAudio() {
  if (audioUnlocked) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  const markUnlocked = () => {
    audioUnlocked = true;
  };

  if (ctx.state === 'suspended') {
    void ctx.resume().then(markUnlocked).catch(() => undefined);
  } else {
    markUnlocked();
  }
}

function scheduleTonePattern(ctx: AudioContext, type: NotificationType) {
  const steps = TONE_STEPS[type];
  const startTime = ctx.currentTime + 0.015;
  const masterGain = ctx.createGain();
  const toneFilter = ctx.createBiquadFilter();
  const delay = ctx.createDelay(0.35);
  const feedback = ctx.createGain();
  const echoGain = ctx.createGain();
  const compressor = ctx.createDynamicsCompressor();

  masterGain.gain.setValueAtTime(0.88, startTime);
  masterGain.gain.exponentialRampToValueAtTime(0.001, startTime + 1.25);
  toneFilter.type = 'highpass';
  toneFilter.frequency.setValueAtTime(type === 'cancelled' ? 160 : 520, startTime);
  delay.delayTime.setValueAtTime(type === 'cancelled' ? 0.08 : 0.12, startTime);
  feedback.gain.setValueAtTime(type === 'cancelled' ? 0.14 : 0.2, startTime);
  echoGain.gain.setValueAtTime(type === 'cancelled' ? 0.16 : 0.24, startTime);
  compressor.threshold.setValueAtTime(-24, startTime);
  compressor.knee.setValueAtTime(24, startTime);
  compressor.ratio.setValueAtTime(6, startTime);
  compressor.attack.setValueAtTime(0.003, startTime);
  compressor.release.setValueAtTime(0.18, startTime);

  masterGain.connect(toneFilter);
  toneFilter.connect(compressor);
  toneFilter.connect(delay);
  delay.connect(feedback);
  feedback.connect(delay);
  delay.connect(echoGain);
  echoGain.connect(compressor);
  compressor.connect(ctx.destination);

  steps.forEach((step) => {
    const startAt = startTime + step.at;
    const endAt = startAt + step.duration;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(masterGain);

    oscillator.type = step.wave ?? 'sine';
    oscillator.frequency.setValueAtTime(step.freq, startAt);
    oscillator.detune.setValueAtTime(-4, startAt);
    oscillator.detune.linearRampToValueAtTime(0, startAt + 0.04);
    gainNode.gain.setValueAtTime(0.0001, startAt);
    gainNode.gain.linearRampToValueAtTime(step.gain ?? 0.14, startAt + 0.006);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, endAt);

    oscillator.start(startAt);
    oscillator.stop(endAt + 0.02);
  });
}

function playChime(type: NotificationType) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const playPattern = () => scheduleTonePattern(ctx, type);

    if (ctx.state === 'suspended') {
      void ctx.resume().then(playPattern).catch(() => undefined);
    } else {
      playPattern();
    }
  } catch {
    // Ignore audio errors so notifications still work.
  }
}

function repairMojibakeText(value: string) {
  if (!utf8Decoder || !MOJIBAKE_PATTERN.test(value)) return value;

  let repaired = value;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (!MOJIBAKE_PATTERN.test(repaired)) break;

    const bytes = Uint8Array.from(
      Array.from(repaired).map((char) => char.charCodeAt(0) & 0xff),
    );
    const decoded = utf8Decoder.decode(bytes);

    if (!decoded || decoded === repaired || decoded.includes('\u0000')) break;
    repaired = decoded;
  }

  return repaired;
}

function normalizeNotification(notif: AppNotification): AppNotification {
  return {
    ...notif,
    title: repairMojibakeText(notif.title),
    body: repairMojibakeText(notif.body),
  };
}

function buildInstantBookingNotification(
  appointment: Record<string, unknown>,
  organizationId: string,
  salonId: string,
): AppNotification | null {
  const appointmentId = typeof appointment.id === 'string' ? appointment.id : null;
  if (!appointmentId) return null;

  return {
    id: `${INSTANT_BOOKING_PREFIX}${appointmentId}`,
    organization_id: organizationId,
    salon_id: salonId,
    type: 'new_booking',
    title: 'Reserva online recibida',
    body: 'Nueva reserva online agregada a la agenda.',
    appointment_id: appointmentId,
    client_id: typeof appointment.client_id === 'string' ? appointment.client_id : null,
    read: false,
    created_at: typeof appointment.created_at === 'string'
      ? appointment.created_at
      : new Date().toISOString(),
  };
}

if (typeof window !== 'undefined') {
  ['click', 'touchstart', 'keydown'].forEach((eventName) => {
    document.addEventListener(eventName, unlockAudio, { passive: true });
  });
}

export async function requestPushPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

function showBrowserNotification(notif: AppNotification) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  try {
    const browserNotification = new Notification(notif.title, {
      body: notif.body,
      icon: '/ISO_TIPOMESTLY.png',
      tag: notif.id,
    });
    setTimeout(() => browserNotification.close(), 8000);
  } catch {
    // Safari and older browsers can fail here. Keep silent.
  }
}

async function syncBirthdayNotifications(salonId: string) {
  await supabase.rpc('sync_birthday_notifications', { p_salon_id: salonId });
}

export function useNotifications() {
  const { user } = useAuth();
  const organizationId = user?.organization_id;
  const salonId = user?.salon_id;

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toast, setToast] = useState<AppNotification | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notificationsRef = useRef<AppNotification[]>([]);
  const hasLoadedOnceRef = useRef(false);
  const lastToastNotificationIdRef = useRef<string | null>(null);
  const lastBirthdaySyncRef = useRef(0);
  const recentlyToastedAppointmentIdsRef = useRef<Map<string, number>>(new Map());

  const triggerToast = useCallback((notif: AppNotification) => {
    if (lastToastNotificationIdRef.current === notif.id) return;

    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(notif);
    toastTimer.current = setTimeout(() => setToast(null), 6500);
    lastToastNotificationIdRef.current = notif.id;

    playChime(notif.type);
    showBrowserNotification(notif);
  }, []);

  const rememberAppointmentToast = useCallback((appointmentId?: string | null) => {
    if (!appointmentId) return;

    const now = Date.now();
    const next = recentlyToastedAppointmentIdsRef.current;
    next.set(appointmentId, now);

    next.forEach((createdAt, id) => {
      if (now - createdAt > STALE_NOTIFICATION_THRESHOLD_MS) {
        next.delete(id);
      }
    });
  }, []);

  const wasAppointmentRecentlyToasted = useCallback((appointmentId?: string | null) => {
    if (!appointmentId) return false;

    const toastedAt = recentlyToastedAppointmentIdsRef.current.get(appointmentId);
    if (!toastedAt) return false;

    if (Date.now() - toastedAt > STALE_NOTIFICATION_THRESHOLD_MS) {
      recentlyToastedAppointmentIdsRef.current.delete(appointmentId);
      return false;
    }

    return true;
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!organizationId || !salonId) {
      setNotifications([]);
      notificationsRef.current = [];
      hasLoadedOnceRef.current = false;
      return;
    }

    const shouldSyncBirthdays = Date.now() - lastBirthdaySyncRef.current > BIRTHDAY_SYNC_INTERVAL_MS;
    if (shouldSyncBirthdays) {
      lastBirthdaySyncRef.current = Date.now();
      void syncBirthdayNotifications(salonId).catch((error) => {
        logger.warn('[Mestly] Birthday sync failed before loading notifications', error);
      });
    }

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('organization_id', organizationId)
      .filter('salon_id', 'eq', salonId)
      .order('created_at', { ascending: false })
      .limit(NOTIFICATION_FETCH_LIMIT);

    if (error) {
      logger.error('[Mestly] Failed to load notifications', error);
      return;
    }

    const nextNotifications = ((data ?? []) as AppNotification[]).map(normalizeNotification);
    const previousNotifications = notificationsRef.current;

    if (hasLoadedOnceRef.current) {
      const incomingNotification = nextNotifications.find((notif) => (
        !notif.read &&
        !previousNotifications.some((previous) => previous.id === notif.id) &&
        Date.now() - new Date(notif.created_at).getTime() <= STALE_NOTIFICATION_THRESHOLD_MS
      ));

      if (incomingNotification) {
        if (
          incomingNotification.type !== 'new_booking' ||
          !wasAppointmentRecentlyToasted(incomingNotification.appointment_id)
        ) {
          triggerToast(incomingNotification);
        }
      }
    } else {
      hasLoadedOnceRef.current = true;
    }

    notificationsRef.current = nextNotifications;
    setNotifications(nextNotifications);
  }, [organizationId, salonId, triggerToast, wasAppointmentRecentlyToasted]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!organizationId || !salonId) return;

    const handleVisibilitySync = () => {
      if (document.visibilityState === 'visible') {
        void loadNotifications();
      }
    };

    const handleFocusSync = () => {
      void loadNotifications();
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadNotifications();
      }
    }, NOTIFICATIONS_REFRESH_INTERVAL_MS);

    window.addEventListener('focus', handleFocusSync);
    document.addEventListener('visibilitychange', handleVisibilitySync);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocusSync);
      document.removeEventListener('visibilitychange', handleVisibilitySync);
    };
  }, [loadNotifications, organizationId, salonId]);

  useEffect(() => {
    if (!organizationId || !salonId) return;

    const channel = supabase
      .channel(`notifications-${organizationId}-${salonId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          const notif = normalizeNotification(payload.new as AppNotification);

          if (notif.organization_id !== organizationId) return;
          if (notif.salon_id !== salonId) return;

          const notifAge = Date.now() - new Date(notif.created_at).getTime();
          if (notifAge > STALE_NOTIFICATION_THRESHOLD_MS) return;

          const wasInstantlyToasted =
            notif.type === 'new_booking' &&
            wasAppointmentRecentlyToasted(notif.appointment_id);

          setNotifications((previous) => {
            const next = [
              notif,
              ...previous.filter((item) => (
                item.id !== notif.id &&
                item.id !== `${INSTANT_BOOKING_PREFIX}${notif.appointment_id ?? ''}`
              )),
            ];
            notificationsRef.current = next.slice(0, NOTIFICATION_FETCH_LIMIT);
            return next.slice(0, NOTIFICATION_FETCH_LIMIT);
          });

          if (!wasInstantlyToasted) {
            triggerToast(notif);
          }
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          const notif = normalizeNotification(payload.new as AppNotification);

          if (notif.organization_id !== organizationId) return;
          if (notif.salon_id !== salonId) return;

          setNotifications((previous) =>
            previous.map((item) => (item.id === notif.id ? notif : item)),
          );
          notificationsRef.current = notificationsRef.current.map((item) => (
            item.id === notif.id ? notif : item
          ));
        },
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          void loadNotifications();
        }
        if (status === 'CHANNEL_ERROR') {
          logger.warn('[Mestly] Realtime channel error - notifications will not update live');
          void loadNotifications();
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [loadNotifications, organizationId, salonId, triggerToast, wasAppointmentRecentlyToasted]);

  useEffect(() => {
    if (!organizationId || !salonId) return;

    const channel = supabase
      .channel(`instant-booking-notifications-${organizationId}-${salonId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointments',
          filter: `organization_id=eq.${organizationId}`,
        },
        (payload) => {
          const appointment = payload.new as Record<string, unknown>;
          const appointmentId = typeof appointment.id === 'string' ? appointment.id : null;

          if (!appointmentId) return;
          if (appointment.organization_id !== organizationId) return;
          if (appointment.salon_id !== salonId) return;
          if (appointment.public_booking !== true) return;
          if (appointment.is_blocked === true) return;
          if (wasAppointmentRecentlyToasted(appointmentId)) return;

          const createdAt = typeof appointment.created_at === 'string'
            ? appointment.created_at
            : new Date().toISOString();
          const appointmentAge = Date.now() - new Date(createdAt).getTime();
          if (appointmentAge > STALE_NOTIFICATION_THRESHOLD_MS) return;

          const alreadyHasNotification = notificationsRef.current.some((item) => (
            item.appointment_id === appointmentId
          ));
          if (alreadyHasNotification) return;

          const instantNotification = buildInstantBookingNotification(
            appointment,
            organizationId,
            salonId,
          );
          if (!instantNotification) return;

          rememberAppointmentToast(appointmentId);

          setNotifications((previous) => {
            if (previous.some((item) => item.appointment_id === appointmentId)) {
              return previous;
            }

            const next = [instantNotification, ...previous].slice(0, NOTIFICATION_FETCH_LIMIT);
            notificationsRef.current = next;
            return next;
          });

          triggerToast(instantNotification);
        },
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          logger.warn('[Mestly] Realtime channel error - instant booking notifications unavailable');
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [
    organizationId,
    salonId,
    rememberAppointmentToast,
    triggerToast,
    wasAppointmentRecentlyToasted,
  ]);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!organizationId || !salonId) return false;

    if (notificationId.startsWith(INSTANT_BOOKING_PREFIX)) {
      setNotifications((previous) =>
        previous.map((item) => (
          item.id === notificationId ? { ...item, read: true } : item
        )),
      );
      notificationsRef.current = notificationsRef.current.map((item) => (
        item.id === notificationId ? { ...item, read: true } : item
      ));
      setToast((current) => (current?.id === notificationId ? null : current));
      return true;
    }

    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .eq('organization_id', organizationId)
      .eq('salon_id', salonId);

    if (error) {
      logger.error('[Mestly] Failed to mark notification as read', error);
      return false;
    }

    setNotifications((previous) =>
      previous.map((item) => (
        item.id === notificationId ? { ...item, read: true } : item
      )),
    );
    notificationsRef.current = notificationsRef.current.map((item) => (
      item.id === notificationId ? { ...item, read: true } : item
    ));
    setToast((current) => (current?.id === notificationId ? null : current));
    return true;
  }, [organizationId, salonId]);

  const markAllRead = useCallback(async () => {
    if (!organizationId || !salonId) return false;

    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('organization_id', organizationId)
      .filter('salon_id', 'eq', salonId)
      .eq('read', false);

    if (error) {
      logger.error('[Mestly] Failed to mark all notifications as read', error);
      return false;
    }

    setNotifications((previous) =>
      previous.map((item) => (item.read ? item : { ...item, read: true })),
    );
    notificationsRef.current = notificationsRef.current.map((item) => (
      item.read ? item : { ...item, read: true }
    ));
    setToast(null);
    return true;
  }, [organizationId, salonId]);

  const dismissToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(null);
  }, []);

  const unreadCount = useMemo(
    () => notifications.reduce((total, notif) => total + (notif.read ? 0 : 1), 0),
    [notifications],
  );

  return {
    notifications,
    unreadCount,
    toast,
    dismissToast,
    markAsRead,
    markAllRead,
  };
}
